import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Image, X, Users } from 'lucide-react';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import type { PostMedia, Profile } from '../types/database';
import { compressMedia } from '../lib/mediaCompression';
import PostAuthorAvatar from './PostAuthorAvatar';
import { useUrlPreviews } from '../hooks/useUrlPreviews';
import { ReactSortable } from 'react-sortablejs';
import FriendSelector from './FriendSelector';

interface EditPostProps {
  postId: string;
  initialDescription: string;
  initialMedia: PostMedia[];
  initialTags?: Profile[];
  onSave: () => void;
  onCancel: () => void;
}

interface SortableFile extends File {
  id: string;
}

interface SortableMedia extends PostMedia {
  id: string;
}

export default function EditPost({ 
  postId,
  initialDescription,
  initialMedia,
  initialTags = [],
  onSave,
  onCancel
}: EditPostProps) {
  const navigate = useNavigate();
  const [description, setDescription] = useState(initialDescription);
  const [mediaFiles, setMediaFiles] = useState<SortableFile[]>([]);
  const [mediaPreviews, setMediaPreviews] = useState<string[]>([]);
  const [existingMedia, setExistingMedia] = useState<SortableMedia[]>(
    initialMedia.map(media => ({
      ...media,
      id: media.id || Math.random().toString(36).substring(2, 10)
    }))
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [taggedFriends, setTaggedFriends] = useState<Profile[]>(initialTags);
  const [showFriendSelector, setShowFriendSelector] = useState(false);
  
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const { extractUrls, fetchUrlMetadata, renderContent } = useUrlPreviews();

  useEffect(() => {
    loadProfile();
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [description]);

  // Process URLs in description
  useEffect(() => {
    if (description) {
      const urls = extractUrls(description);
      urls.forEach(url => {
        fetchUrlMetadata(url);
      });
    }
  }, [description, extractUrls, fetchUrlMetadata]);

  // Handle drag and drop
  useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
      setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer?.files || []);
      await handleFiles(files);
    };

    const dropZone = dropZoneRef.current;
    if (dropZone) {
      dropZone.addEventListener('dragover', handleDragOver);
      dropZone.addEventListener('dragleave', handleDragLeave);
      dropZone.addEventListener('drop', handleDrop);
    }

    return () => {
      if (dropZone) {
        dropZone.removeEventListener('dragover', handleDragOver);
        dropZone.removeEventListener('dragleave', handleDragLeave);
        dropZone.removeEventListener('drop', handleDrop);
      }
    };
  }, []);

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (profile) {
          setProfile(profile);
        }
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  const handleFiles = async (files: File[]) => {
    if (files.length === 0) return;

    if (mediaFiles.length + existingMedia.length + files.length > 10) {
      setError('Maximum 10 files allowed');
      return;
    }

    try {
      const processedFiles: SortableFile[] = [];
      const previews: string[] = [];

      for (const file of files) {
        if (file.size > 50 * 1024 * 1024) {
          throw new Error('File size must be less than 50MB');
        }

        const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
        const isMovFile = fileExt === 'mov';
        let fileType = file.type;
        if (isMovFile) {
          fileType = 'video/quicktime';
        }

        const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        const allowedVideoTypes = [
          'video/mp4',
          'video/webm',
          'video/quicktime',
          'video/x-quicktime',
          'video/mov',
          'video/x-msvideo',
          'application/octet-stream'
        ];

        const isImage = allowedImageTypes.includes(fileType);
        const isVideo = allowedVideoTypes.includes(fileType) || isMovFile;
        
        if (!isImage && !isVideo) {
          throw new Error('Unsupported file type. Please use JPEG, PNG, GIF, WEBP for images, or MP4, WEBM, MOV for videos.');
        }

        let processedFile: File;
        if (isMovFile) {
          processedFile = new File([file], file.name, { type: 'video/quicktime' });
        } else if (isImage) {
          processedFile = await compressMedia(file);
        } else {
          processedFile = file;
        }

        const sortableFile = Object.assign(processedFile, {
          id: Math.random().toString(36).substring(2, 10)
        }) as SortableFile;

        processedFiles.push(sortableFile);
        const previewUrl = URL.createObjectURL(processedFile);
        previews.push(previewUrl);
      }

      setMediaFiles(prev => [...prev, ...processedFiles]);
      setMediaPreviews(prev => [...prev, ...previews]);
      setError(null);
    } catch (err: any) {
      console.error('Error processing media:', err);
      setError(err.message || 'Failed to process media. Please try again.');
    }
  };

  const handleMediaSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    await handleFiles(files);
    if (event.target.value) {
      event.target.value = '';
    }
  };

  const removeMedia = (index: number, isExisting: boolean = false) => {
    if (isExisting) {
      setExistingMedia(prev => prev.filter((_, i) => i !== index));
    } else {
      URL.revokeObjectURL(mediaPreviews[index]);
      setMediaFiles(prev => prev.filter((_, i) => i !== index));
      setMediaPreviews(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim() && mediaFiles.length === 0 && existingMedia.length === 0) return;

    let uploadedFilePaths: string[] = [];

    try {
      setLoading(true);
      setError(null);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Please sign in to update the post');

      // Update post description
      const { error: postError } = await supabase
        .from('posts')
        .update({ description: description.trim() || null })
        .eq('id', postId)
        .eq('user_id', user.id);

      if (postError) throw postError;

      // Delete all existing media entries for this post
      const { error: deleteError } = await supabase
        .from('post_media')
        .delete()
        .eq('post_id', postId);

      if (deleteError) throw deleteError;

      // Delete existing tags
      console.log('Deleting existing tags for post:', postId);

      const { error: tagDeleteError } = await supabase
        .from('post_tags')
        .delete()
        .eq('post_id', postId);

      if (tagDeleteError) {
        console.error('Tag deletion error:', tagDeleteError);
        throw tagDeleteError;
      }

      console.log('Existing tags deleted successfully');

      // Re-insert existing media with updated positions
      if (existingMedia.length > 0) {
        const { error: existingMediaError } = await supabase
          .from('post_media')
          .insert(
            existingMedia.map((media, index) => ({
              post_id: postId,
              url: media.url,
              type: media.type,
              position: index
            }))
          );

        if (existingMediaError) throw existingMediaError;
      }

      // Upload and insert new media files
      if (mediaFiles.length > 0) {
        const mediaUploads = mediaFiles.map(async (file, index) => {
          const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
          const timestamp = Date.now();
          const randomString = Math.random().toString(36).substring(2, 10);
          const filePath = `${user.id}/${timestamp}-${randomString}.${fileExt}`;
          uploadedFilePaths.push(filePath);

          const { error: uploadError } = await supabase.storage
            .from('posts')
            .upload(filePath, file, {
              contentType: file.type || 'video/quicktime',
              cacheControl: '3600',
              upsert: true
            });

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('posts')
            .getPublicUrl(filePath);

          const { error: mediaError } = await supabase
            .from('post_media')
            .insert({
              post_id: postId,
              url: publicUrl,
              type: file.type.startsWith('image/') ? 'image' : 'video',
              position: existingMedia.length + index
            });

          if (mediaError) throw mediaError;
        });

        await Promise.all(mediaUploads);
      }

      // Create new post tags
      if (taggedFriends.length > 0) {
        const tagInserts = taggedFriends.map(friend => ({
          post_id: postId,
          tagged_user_id: friend.id,
          tagged_by_user_id: user.id
        }));

        console.log('Inserting new tags:', tagInserts);

        const { data: tagData, error: tagError } = await supabase
          .from('post_tags')
          .insert(tagInserts)
          .select();

        if (tagError) {
          console.error('Tag insertion error:', tagError);
          throw tagError;
        }

        console.log('New tags inserted successfully:', tagData);
      }

      onSave();
    } catch (err: any) {
      console.error('Post update error:', err);
      setError(err.message || 'Failed to update post. Please try again.');
      
      if (uploadedFilePaths.length > 0) {
        try {
          await supabase.storage
            .from('posts')
            .remove(uploadedFilePaths);
        } catch (cleanupError) {
          console.error('Failed to clean up media:', cleanupError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMediaClick = () => {
    if (mediaInputRef.current) {
      mediaInputRef.current.click();
    }
  };

  const renderTaggedFriends = () => {
    if (taggedFriends.length === 0) return null;

    const displayNames = taggedFriends.slice(0, 3).map(friend => friend.full_name);
    const remainingCount = taggedFriends.length - 3;

    let text = 'with ';
    if (taggedFriends.length === 1) {
      text += displayNames[0];
    } else if (taggedFriends.length === 2) {
      text += `${displayNames[0]} and ${displayNames[1]}`;
    } else if (taggedFriends.length === 3) {
      text += `${displayNames[0]}, ${displayNames[1]} and ${displayNames[2]}`;
    } else {
      text += `${displayNames[0]}, ${displayNames[1]} and ${remainingCount} others`;
    }

    return (
      <div className="mt-2 text-sm text-gray-600 flex items-center">
        <Users className="h-4 w-4 mr-1" />
        <span>{text}</span>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <form onSubmit={handleSubmit} className="p-4">
        <div 
          ref={dropZoneRef}
          className={cn(
            "flex gap-4",
            isDragging && "relative after:absolute after:inset-0 after:bg-blue-50 after:border-2 after:border-dashed after:border-blue-500 after:rounded-lg"
          )}
        >
          <PostAuthorAvatar profile={profile} />
          <div className="flex-1 min-w-0">
            <textarea
              ref={textareaRef}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's on your mind?"
              className="w-full p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[120px] overflow-hidden break-words"
              style={{ height: 'auto' }}
            />
            {description && (
              <div className="mt-2 w-full">
                {renderContent(description, true)}
              </div>
            )}
            {renderTaggedFriends()}
          </div>
        </div>

        {(mediaPreviews.length > 0 || existingMedia.length > 0) && (
          <div className="mt-4 pl-14">
            {/* Existing media */}
            {existingMedia.length > 0 && (
              <ReactSortable
                list={existingMedia}
                setList={setExistingMedia}
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-2"
                animation={150}
                ghostClass="opacity-50"
                handle=".media-drag-handle"
                group="media-items"
              >
                {existingMedia.map((media, index) => (
                  <div key={media.id} className="relative aspect-square group">
                    <div className="absolute inset-0 media-drag-handle cursor-move opacity-0 group-hover:opacity-100 transition-opacity z-10 flex items-center justify-center">
                      <div className="bg-black bg-opacity-20 absolute inset-0"></div>
                      <div className="text-white font-medium text-sm z-20">Drag to reorder</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeMedia(index, true)}
                      className="absolute top-2 right-2 p-1 bg-gray-800 bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-opacity z-20"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    {media.type === 'image' ? (
                      <img
                        src={media.url}
                        alt={`Media ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <video
                        src={media.url}
                        className="w-full h-full object-cover rounded-lg"
                        controls
                      />
                    )}
                  </div>
                ))}
              </ReactSortable>
            )}

            {/* New media */}
            {mediaFiles.length > 0 && (
              <ReactSortable
                list={mediaFiles}
                setList={setMediaFiles as any}
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
                animation={150}
                ghostClass="opacity-50"
                handle=".media-drag-handle"
                group="media-items"
                onEnd={(evt) => {
                  // Update previews to match the new order
                  const newPreviews = [...mediaPreviews];
                  const movedPreview = newPreviews.splice(evt.oldIndex, 1)[0];
                  newPreviews.splice(evt.newIndex, 0, movedPreview);
                  setMediaPreviews(newPreviews);
                }}
              >
                {mediaFiles.map((file, index) => (
                  <div key={file.id} className="relative aspect-square group">
                    <div className="absolute inset-0 media-drag-handle cursor-move opacity-0 group-hover:opacity-100 transition-opacity z-10 flex items-center justify-center">
                      <div className="bg-black bg-opacity-20 absolute inset-0"></div>
                      <div className="text-white font-medium text-sm z-20">Drag to reorder</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeMedia(index)}
                      className="absolute top-2 right-2 p-1 bg-gray-800 bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-opacity z-20"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    {file.type.startsWith('image/') ? (
                      <img
                        src={mediaPreviews[index]}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <video
                        src={mediaPreviews[index]}
                        className="w-full h-full object-cover rounded-lg"
                        controls
                      />
                    )}
                  </div>
                ))}
              </ReactSortable>
            )}
          </div>
        )}

        {error && (
          <p className="mt-2 pl-14 text-sm text-red-600">{error}</p>
        )}

        <div className="mt-4 flex items-center justify-end space-x-2">
          <input
            ref={mediaInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.webm,.mov,video/*"
            onChange={handleMediaSelect}
            disabled={loading}
            className="hidden"
            multiple
          />
          
          <button
            type="button"
            onClick={handleMediaClick}
            className={cn(
              "p-2 rounded-full cursor-pointer hover:bg-gray-100 transition-colors",
              loading && "opacity-50 cursor-not-allowed"
            )}
            disabled={loading}
          >
            <Image className="h-5 w-5 text-gray-500" />
          </button>

          <button
            type="button"
            onClick={() => setShowFriendSelector(true)}
            className={cn(
              "p-2 rounded-full cursor-pointer hover:bg-gray-100 transition-colors",
              loading && "opacity-50 cursor-not-allowed",
              taggedFriends.length > 0 && "text-blue-500 bg-blue-50"
            )}
            disabled={loading}
          >
            <Users className="h-5 w-5" />
          </button>

          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading || (!description.trim() && mediaFiles.length === 0 && existingMedia.length === 0)}
            className={cn(
              "px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>

      <FriendSelector
        isOpen={showFriendSelector}
        onClose={() => setShowFriendSelector(false)}
        onSelectFriends={setTaggedFriends}
        selectedFriends={taggedFriends}
      />
    </div>
  );
}